/**
 * 会計年度関連のユーティリティ関数
 */

/**
 * 指定した日付が所属する会計年度を取得
 * @param date 対象の日付
 * @param fiscalYearStartMonth 会計年度開始月 (1-12)
 * @returns 会計年度（西暦）
 */
export const getFiscalYear = (date: Date, fiscalYearStartMonth: number): number => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // getMonth() は 0-11 なので +1

  if (month >= fiscalYearStartMonth) {
    return year;
  } else {
    return year - 1;
  }
};

/**
 * 会計年度の開始日を取得
 * @param fiscalYear 会計年度
 * @param fiscalYearStartMonth 会計年度開始月 (1-12)
 * @returns 会計年度開始日
 */
export const getFiscalYearStartDate = (fiscalYear: number, fiscalYearStartMonth: number): Date => {
  return new Date(fiscalYear, fiscalYearStartMonth - 1, 1);
};

/**
 * 会計年度の終了日を取得
 * @param fiscalYear 会計年度
 * @param fiscalYearStartMonth 会計年度開始月 (1-12)
 * @returns 会計年度終了日
 */
export const getFiscalYearEndDate = (fiscalYear: number, fiscalYearStartMonth: number): Date => {
  const nextFiscalYearStartMonth = fiscalYearStartMonth === 1 ? 12 : fiscalYearStartMonth - 1;
  const nextYear = fiscalYearStartMonth === 1 ? fiscalYear : fiscalYear + 1;
  
  // JavaScriptのDateは0-basedなので、nextFiscalYearStartMonth - 1を渡す
  const nextMonthFirstDay = new Date(nextYear, nextFiscalYearStartMonth - 1, 1);
  return new Date(nextMonthFirstDay.getTime() - 24 * 60 * 60 * 1000);
};

/**
 * 現在の会計年度を取得
 * @param fiscalYearStartMonth 会計年度開始月 (1-12)
 * @returns 現在の会計年度
 */
export const getCurrentFiscalYear = (fiscalYearStartMonth: number): number => {
  return getFiscalYear(new Date(), fiscalYearStartMonth);
};

/**
 * 会計年度の表示名を取得
 * @param fiscalYear 会計年度
 * @param fiscalYearStartMonth 会計年度開始月 (1-12)
 * @returns 会計年度の表示名 (例: "2024年度 (2024/04-2025/03)")
 */
export const getFiscalYearDisplayName = (fiscalYear: number, fiscalYearStartMonth: number): string => {
  const startDate = getFiscalYearStartDate(fiscalYear, fiscalYearStartMonth);
  const endDate = getFiscalYearEndDate(fiscalYear, fiscalYearStartMonth);
  
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}/${month}`;
  };
  
  return `${fiscalYear}年度 (${formatDate(startDate)}-${formatDate(endDate)})`;
};

/**
 * 指定した会計年度の月リストを取得
 * @param fiscalYear 会計年度
 * @param fiscalYearStartMonth 会計年度開始月 (1-12)
 * @returns 月のリスト（Date オブジェクト）
 */
export const getFiscalYearMonths = (fiscalYear: number, fiscalYearStartMonth: number): Date[] => {
  const months: Date[] = [];
  
  for (let i = 0; i < 12; i++) {
    const month = (fiscalYearStartMonth - 1 + i) % 12;
    const year = fiscalYear + (fiscalYearStartMonth + i > 12 ? 1 : 0);
    months.push(new Date(year, month, 1));
  }
  
  return months;
};